import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'react-bootstrap';
import { MAX_LENGTH, MODAL, QUERY_KEYS } from '../../../constants';
import TextEditor from '../../shared/TextEditor';
import useWindowSize from '../../../hooks/useWindowSize';
import { popup } from '../../../utils';
import {
    createAccountReviewAPI,
    updateAccountReviewAPI,
} from '../../../utils/apiServices/accountReviewAPI';
import { countCharactersExcludingSpaces, minifyHtml } from './helpers';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const CreateAccountReviewModal = ({
    showModal,
    setShowModal,
    accountId,
    isEditMode = false,
    id,
    ...props
}) => {
    const { width } = useWindowSize();
    const queryClient = useQueryClient();
    const textEditorHeight = width > 987 ? 700 : 780;
    const [titleError, setTitleError] = useState(false);
    const initialContentRef = useRef(props?.content || '');
    const initialTitleRef = useRef(props?.title || '');

    const editorRef = useRef(null);
    const contentRef = useRef('');
    const titleRef = useRef('');

    const onHideHandler = () => {
        if (!isEditMode) {
            initialContentRef.current = '';
            initialTitleRef.current = '';
        }
        titleRef.current = null;
        contentRef.current = null;
        editorRef.current = null;
        setTitleError(false);
        setShowModal(false);
    };

    useEffect(() => {
        if (props?.content && props?.title && isEditMode) {
            initialContentRef.current = props?.content;
            initialTitleRef.current = props?.title;
            contentRef.current = props?.content;
            titleRef.current = props?.title;
        }
    }, [isEditMode, props?.content, props?.title, contentRef, titleRef]);

    const onSave = async () => {
        const trimTitle = titleRef.current?.trim();
        const contentLength = countCharactersExcludingSpaces(contentRef.current);

        if (trimTitle?.length < 3 || !trimTitle) {
            setTitleError(true);
            popup('error', 'Title must be at least 3 characters long');
            return;
        }

        if (contentLength < 3) {
            popup('error', 'Content must be at least 3 characters long');
            return;
        }

        if (contentLength > 20000) {
            popup('error', 'Max 20,000 characters exceeded!');
            return;
        }

        if (
            trimTitle === initialTitleRef.current &&
            contentRef.current === initialContentRef.current
        ) {
            onHideHandler();
            popup('info', 'No changes are made!');
            return;
        }

        const content = minifyHtml(contentRef.current);

        if (isEditMode) {
           editAccountReviewMutation.mutate({title :trimTitle, content});
        } else {
            newAccountReviewMutation.mutate({title: trimTitle, content});
        }
    };

    const editAccountReviewMutation = useMutation({
        mutationFn: async ({title, content}) =>
            await updateAccountReviewAPI({ id, accountId, title, content }),
        onSuccess: (res) => {
            queryClient.invalidateQueries([QUERY_KEYS.ACCOUNT_REVIEW_BY_ID, accountId]);
            popup('success', res.message);
            onHideHandler();
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const newAccountReviewMutation = useMutation({
        mutationFn: async ({title, content}) =>
            await createAccountReviewAPI({ accountId, title, content }),
        onSuccess: (res) => {
            queryClient.invalidateQueries([QUERY_KEYS.ACCOUNT_REVIEWS, accountId]);
            popup('success', res.message);
            onHideHandler();
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    return (
        <Modal
            show={showModal}
            onHide={onHideHandler}
            size='xl'
            dialogClassName='overflow-hidden'
            fullscreen='lg-down'
            centered
            key={accountId}
        >
            <Modal.Header closeButton>
                <Modal.Title className='me-3' style={{ fontSize: '18px' }}>
                    {isEditMode ? 'Update Account Review' : 'Create Account Review'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ overflow: 'hidden' }}>
                <div className='d-flex flex-column gap-2'>
                    <div className='position-relative'>
                        <label htmlFor='title'>{'Title:'}</label>
                        <input
                            maxLength={MAX_LENGTH.CLIENT_NAME}
                            type='text'
                            name='title'
                            onChange={(e) => {
                                setTitleError(false);
                                titleRef.current = e.target.value;
                            }}
                            className={`input-area-set form-control ${titleError ? 'border border-danger-subtle border-2' : ''}`}
                            id='title'
                            style={{ fontSize: '15px' }}
                            placeholder={'Title...'}
                            required={true}
                            defaultValue={props?.title || ''}
                        />
                        {titleError && (
                            <p className='position-absolute top-0 end-0 error-message'>
                                {'Minimum 3 characters required'}
                            </p>
                        )}
                    </div>
                    <div>
                        <label htmlFor='content'>{'Content:'}</label>
                        <TextEditor
                            ref={editorRef}
                            content={props?.content || contentRef.current}
                            onBlur={(content) => (contentRef.current = content)}
                            allowResizeY={false}
                            height={textEditorHeight}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className='d-flex gap-3 w-100 align-items-center justify-content-end modal-btn mt-3'>
                    <Button
                        type='button'
                        className='ssc-secondary-white-btn '
                        onClick={onHideHandler}
                        style={{ height: '40px', fontSize: '15px' }}
                        title={MODAL.CANCEL_BUTTON}
                        disabled={editAccountReviewMutation.isPending || newAccountReviewMutation.isPending}
                    >
                        {MODAL.CANCEL_BUTTON}
                    </Button>

                    <Button
                        type='submit'
                        onClick={onSave}
                        className='table-footer-btn'
                        style={{ height: '40px' }}
                        disabled={editAccountReviewMutation.isPending || newAccountReviewMutation.isPending}
                    >
                        {isEditMode ? 'Update' : 'Save'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

CreateAccountReviewModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    setShowModal: PropTypes.func.isRequired,
};

export default CreateAccountReviewModal;
